import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";
import type {
  Approval,
  ConfigDashboard,
  ConfigVersion,
  NamespaceAtVersion,
  NamespaceDetail,
  ProposeConfigInput,
} from "../../api/config-contracts";

const CONFIG = ["config"];
const APPROVALS = ["approvals"];
const nsKey = (namespace: string) => [...CONFIG, namespace];

/** GET /v1/config — every namespace + governanceLevel + current ACTIVE. config:read@all. */
export function useConfigDashboard() {
  const api = useApi();
  return useQuery({
    queryKey: [...CONFIG, "dashboard"],
    queryFn: () => api.get<ConfigDashboard>("/v1/config"),
  });
}

/** GET /v1/config/:namespace — ACTIVE + full history + governanceLevel. */
export function useNamespace(namespace: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: namespace ? nsKey(namespace) : [...CONFIG, "none"],
    enabled: namespace !== null,
    queryFn: () => api.get<NamespaceDetail>(`/v1/config/${namespace}`),
  });
}

/** GET /v1/config/:namespace?at=<ISO> — the version active at that instant (the
 *  reconstructability read). Distinct, narrower shape: { namespace, at, version }. */
export function useNamespaceAt(namespace: string, at: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: [...nsKey(namespace), "at", at],
    enabled: at !== null && at !== "",
    queryFn: () =>
      api.get<NamespaceAtVersion>(`/v1/config/${namespace}?at=${encodeURIComponent(at!)}`),
  });
}

/** POST /v1/config/:namespace — propose a new version (config:propose@all, audited).
 *  Opens DRAFT (NORMAL_ADMIN) or PENDING_APPROVAL (FOUR_EYES). The server validates
 *  the payload against the namespace guardrails — never validated client-side. */
export function useProposeConfig(namespace: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProposeConfigInput) =>
      api.post<ConfigVersion>(`/v1/config/${namespace}`, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: CONFIG }),
  });
}

/** POST /v1/config/:namespace/versions/:v/activate — config:activate@all, audited.
 *  NORMAL_ADMIN activates directly; FOUR_EYES passes the approved `approvalId` (the
 *  foundation client attaches it as x-approval-id) and the server consumes it
 *  (approver ≠ executor). The originator completes their own activation (AC3). */
export function useActivateConfig(namespace: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ version, approvalId }: { version: number; approvalId?: string }) =>
      api.post<ConfigVersion>(
        `/v1/config/${namespace}/versions/${version}/activate`,
        undefined,
        approvalId ? { approvalId } : {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CONFIG });
      void qc.invalidateQueries({ queryKey: APPROVALS });
    },
  });
}

/** POST /v1/config/:namespace/versions/:v/reject — a DRAFT/PENDING proposal that can
 *  never activate after. config:activate@all, audited config.reject. */
export function useRejectConfig(namespace: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ version, reason }: { version: number; reason?: string }) =>
      api.post<ConfigVersion>(`/v1/config/${namespace}/versions/${version}/reject`, { reason }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: CONFIG }),
  });
}

// ── GA1 Approval API hooks (mirror the console's GA1 cycle — the same ApiClient,
//    the foundation governance client; never a parallel flow). ──

/** The requester's own approvals — so the proposer sees "approved → ready to activate". */
export function useMyApprovals() {
  const api = useApi();
  return useQuery({
    queryKey: [...APPROVALS, "mine"],
    queryFn: () => api.get<Approval[]>("/v1/approvals/mine"),
  });
}

/** The approver's inbox — PENDING approvals this caller may grant. The server returns
 *  only what they're authorized to act on AND excludes their own requests (≠ requester),
 *  so a proposer is structurally never offered approval of their own proposal. */
export function useAwaitingMe() {
  const api = useApi();
  return useQuery({
    queryKey: [...APPROVALS, "awaiting-me"],
    queryFn: () => api.get<Approval[]>("/v1/approvals/awaiting-me"),
  });
}

function useInvalidateApprovals() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: APPROVALS });
}

/** POST /v1/approvals — open a config.activate approval for a PENDING_APPROVAL version. */
export function useRequestApproval() {
  const api = useApi();
  const invalidate = useInvalidateApprovals();
  return useMutation({
    mutationFn: (input: { action: string; resourceId: string }) =>
      api.post<Approval>("/v1/approvals", input),
    onSettled: invalidate,
  });
}

/** POST /v1/approvals/:id/approve — the second person grants (config:approve, ≠ requester). */
export function useApproveApproval() {
  const api = useApi();
  const invalidate = useInvalidateApprovals();
  return useMutation({
    mutationFn: (approvalId: string) => api.post<Approval>(`/v1/approvals/${approvalId}/approve`),
    onSettled: invalidate,
  });
}

/** POST /v1/approvals/:id/reject. */
export function useRejectApproval() {
  const api = useApi();
  const invalidate = useInvalidateApprovals();
  return useMutation({
    mutationFn: ({ approvalId, reason }: { approvalId: string; reason?: string }) =>
      api.post<Approval>(`/v1/approvals/${approvalId}/reject`, { reason }),
    onSettled: invalidate,
  });
}
